import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Save, TrendingUp, TrendingDown, Target, Users, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { SwissCard, SectionHeader, KpiCard } from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const p$ = (s) => parseFloat(String(s || '0').replace(/[$,]/g, '')) || 0;

const SERVICE_LINES = [
  { code: 'AT', label: 'Airport Transfer' }, { code: 'PL', label: 'Point to Lux' },
  { code: 'CC', label: 'Chauffeur Concierge' }, { code: 'EX', label: 'Exotic Driven' },
  { code: 'GR', label: 'Group / Sprinter' }, { code: 'WD', label: 'Wedding' },
  { code: 'CO', label: 'Corporate' }, { code: 'EV', label: 'Event' }, { code: 'DR', label: 'Driving Rental' },
];
const CHANNELS = ['Direct', 'Referral', 'Driven Collective', 'Corporate Account', 'Platform (Turo/etc)', 'Other'];
const CONSULT_TRACKS = ['Discovery Sessions', 'Track A — Builder', 'Track B — Personal', 'Track C — Executive', 'Track D — Accelerator'];

const getMonthKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

const MonthlyPLTab = () => {
  const [monthKey, setMonthKey] = useState(getMonthKey());
  const [summary, setSummary] = useState({ bookings: '', gross: '', driver_cost: '', goal: '', net: '' });
  const [lastMonthNet, setLastMonthNet] = useState('');
  const [weeks, setWeeks] = useState([1, 2, 3, 4, 5].map(w => ({ week: `Week ${w}`, jobs: '', gross: '', driver_cost: '', net: '', outstanding: '' })));
  const [serviceLines, setServiceLines] = useState(SERVICE_LINES.map(s => ({ ...s, jobs: '', gross: '', pct: '' })));
  const [channels, setChannels] = useState(CHANNELS.map(c => ({ channel: c, revenue: '', pct: '' })));
  const [membership, setMembership] = useState({ lobby: { members: '', revenue: '' }, condo: { members: '', revenue: '' }, penthouse: { members: '', revenue: '' }, total_mrr: '', total_members: '' });
  const [consulting, setConsulting] = useState(CONSULT_TRACKS.map(t => ({ track: t, sessions: '', revenue: '' })));
  const [accd, setAccd] = useState({ new_enrollments: '', active_students: '', revenue: '', dc_active_drivers: '', dc_new_onboarded: '', dc_hours: '', dc_total_paid: '' });
  const [topClients, setTopClients] = useState(['', '', '', '', '']);
  const [collections, setCollections] = useState(['', '', '', '']);
  const [notes, setNotes] = useState(['', '', '', '']);
  const [running, setRunning] = useState({ year: '', gross_ytd: '', net_ytd: '', remaining: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/command-center/ops/monthly/${monthKey}`);
      const d = await r.json();
      if (d.summary && Object.keys(d.summary).length) setSummary(d.summary);
      if (d.last_month_net) setLastMonthNet(d.last_month_net);
      if (d.weeks?.length) setWeeks(d.weeks);
      if (d.service_lines?.length) setServiceLines(d.service_lines);
      if (d.channels?.length) setChannels(d.channels);
      if (d.membership) setMembership(d.membership);
      if (d.consulting?.length) setConsulting(d.consulting);
      if (d.accd) setAccd(d.accd);
      if (d.top_clients?.length) setTopClients(d.top_clients);
      if (d.collections?.length) setCollections(d.collections);
      if (d.notes?.length) setNotes(d.notes);
      if (d.running) setRunning(d.running);
    } catch {}
  }, [monthKey]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/command-center/ops/monthly/${monthKey}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary, last_month_net: lastMonthNet, weeks, service_lines: serviceLines, channels, membership, consulting, accd, top_clients: topClients, collections, notes, running }),
      });
      toast.success('Monthly P&L saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  // Goal progress
  const gross = p$(summary.gross);
  const goal = p$(summary.goal);
  const goalPct = goal > 0 ? Math.min(100, Math.round((gross / goal) * 100)) : 0;
  const remaining = Math.max(0, goal - gross);

  // MoM
  const thisNet = p$(summary.net);
  const prevNet = p$(lastMonthNet);
  const momChange = thisNet - prevNet;
  const momTrend = momChange > 0 ? 'up' : momChange < 0 ? 'down' : 'flat';

  return (
    <div className="space-y-5" data-testid="monthly-pl-tab">
      <SectionHeader
        overline="Comprehensive monthly financial statement"
        title="Monthly P&L"
        action={
          <div className="flex items-center gap-2">
            <Input type="month" value={monthKey} onChange={e => setMonthKey(e.target.value)} className="h-9 text-xs w-40" />
            <button onClick={save} disabled={saving} className="h-9 px-4 bg-[#E52B20] hover:bg-[#C92218] text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-50">
              <Save className="h-3 w-3" /> Save
            </button>
          </div>
        }
      />

      {/* Summary + Goal */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[{ label: 'Total Bookings', key: 'bookings' }, { label: 'Gross Revenue', key: 'gross' }, { label: 'Driver Cost', key: 'driver_cost' }, { label: 'Monthly Goal', key: 'goal' }, { label: 'Net to ACE', key: 'net' }].map((f, i) => (
          <SwissCard key={i} className="p-3">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#666] mb-1">{f.label}</p>
            <Input value={summary[f.key] || ''} onChange={e => setSummary(p => ({ ...p, [f.key]: e.target.value }))} placeholder="$0" className="h-8 text-xl font-black border-0 bg-transparent p-0 focus-visible:ring-0" />
          </SwissCard>
        ))}
      </div>

      {/* Goal Progress Bar */}
      {goal > 0 && (
        <SwissCard className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#666] flex items-center gap-1"><Target className="h-3 w-3 text-[#E52B20]" /> Goal Progress</span>
            <span className="text-sm font-black text-[#E52B20]">{goalPct}%</span>
          </div>
          <div className="h-2 bg-[#F9F9FA] border border-[#E5E5E5] overflow-hidden">
            <div className="h-full bg-[#E52B20] transition-all" style={{ width: `${goalPct}%` }} />
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-[#666] font-mono">
            <span>{fmt(gross)} earned</span><span>{fmt(remaining)} remaining</span>
          </div>
        </SwissCard>
      )}

      {/* Month over Month */}
      <SwissCard className="p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#666] mb-3">Month-over-Month</p>
        <div className="grid grid-cols-4 gap-3">
          <div><p className="text-[10px] uppercase tracking-[0.15em] text-[#666] font-bold mb-1">Last Month Net</p>
            <Input value={lastMonthNet} onChange={e => setLastMonthNet(e.target.value)} placeholder="$0" className="h-9 text-xs" /></div>
          <div><p className="text-[10px] uppercase tracking-[0.15em] text-[#666] font-bold mb-1">This Month Net</p>
            <div className="h-9 flex items-center text-sm font-black font-mono">{fmt(thisNet)}</div></div>
          <div><p className="text-[10px] uppercase tracking-[0.15em] text-[#666] font-bold mb-1">Change</p>
            <div className={`h-9 flex items-center text-sm font-black font-mono ${momChange >= 0 ? 'text-[#039855]' : 'text-[#E52B20]'}`}>{momChange >= 0 ? '+' : ''}{fmt(momChange)}</div></div>
          <div><p className="text-[10px] uppercase tracking-[0.15em] text-[#666] font-bold mb-1">Trend</p>
            <div className="h-9 flex items-center gap-1">{momTrend === 'up' ? <TrendingUp className="h-5 w-5 text-[#039855]" /> : momTrend === 'down' ? <TrendingDown className="h-5 w-5 text-[#E52B20]" /> : <span className="text-[#666]">—</span>}</div></div>
        </div>
      </SwissCard>

      {/* Week by Week */}
      <SwissCard className="p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#666] mb-3">Week by Week</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr>
              <th className="text-left py-2 text-[10px] text-[#666] uppercase tracking-[0.15em] font-bold border-b-2 border-[#0A0A0A]">Week</th>
              <th className="text-center py-2 text-[10px] text-[#666] uppercase tracking-[0.15em] font-bold w-14 border-b-2 border-[#0A0A0A]">Jobs</th>
              <th className="text-center py-2 text-[10px] text-[#666] uppercase tracking-[0.15em] font-bold w-24 border-b-2 border-[#0A0A0A]">Gross</th>
              <th className="text-center py-2 text-[10px] text-[#666] uppercase tracking-[0.15em] font-bold w-24 border-b-2 border-[#0A0A0A]">Driver Cost</th>
              <th className="text-center py-2 text-[10px] text-[#666] uppercase tracking-[0.15em] font-bold w-24 border-b-2 border-[#0A0A0A]">Net</th>
              <th className="text-center py-2 text-[10px] text-[#666] uppercase tracking-[0.15em] font-bold w-24 border-b-2 border-[#0A0A0A]">Outstanding</th>
            </tr></thead>
            <tbody>{weeks.map((w, i) => (
              <tr key={i} className="border-b border-[#E5E5E5]">
                <td className="py-2 font-bold">{w.week}</td>
                <td className="py-2"><Input type="number" value={w.jobs} onChange={e => setWeeks(p => p.map((x, j) => j === i ? { ...x, jobs: e.target.value } : x))} className="h-7 text-xs text-center w-12" /></td>
                <td className="py-2"><Input value={w.gross} onChange={e => setWeeks(p => p.map((x, j) => j === i ? { ...x, gross: e.target.value } : x))} placeholder="$0" className="h-7 text-xs text-center w-20" /></td>
                <td className="py-2"><Input value={w.driver_cost} onChange={e => setWeeks(p => p.map((x, j) => j === i ? { ...x, driver_cost: e.target.value } : x))} placeholder="$0" className="h-7 text-xs text-center w-20" /></td>
                <td className="py-2"><Input value={w.net} onChange={e => setWeeks(p => p.map((x, j) => j === i ? { ...x, net: e.target.value } : x))} placeholder="$0" className="h-7 text-xs text-center w-20" /></td>
                <td className="py-2"><Input value={w.outstanding} onChange={e => setWeeks(p => p.map((x, j) => j === i ? { ...x, outstanding: e.target.value } : x))} placeholder="$0" className="h-7 text-xs text-center w-20" /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </SwissCard>

      {/* Revenue by Service + Channel */}
      <div className="grid lg:grid-cols-2 gap-4">
        <SwissCard className="p-4">
          <h3 className="text-sm font-bold mb-3">Revenue by Service Line</h3>
          <table className="w-full text-xs"><thead><tr className="border-b border-gray-200">
            <th className="text-left py-1 text-[9px] text-gray-500 uppercase">Service</th>
            <th className="text-center py-1 text-[9px] text-gray-500 uppercase w-12">Jobs</th>
            <th className="text-center py-1 text-[9px] text-gray-500 uppercase w-20">Gross</th>
            <th className="text-center py-1 text-[9px] text-gray-500 uppercase w-12">%</th>
          </tr></thead><tbody>{serviceLines.map((s, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="py-1"><span className="font-bold text-red-600">{s.code}</span> <span className="text-gray-400">{s.label}</span></td>
              <td className="py-1"><Input type="number" value={s.jobs} onChange={e => setServiceLines(p => p.map((x, j) => j === i ? { ...x, jobs: e.target.value } : x))} className="h-6 text-[10px] text-center w-10" /></td>
              <td className="py-1"><Input value={s.gross} onChange={e => setServiceLines(p => p.map((x, j) => j === i ? { ...x, gross: e.target.value } : x))} className="h-6 text-[10px] text-center w-16" /></td>
              <td className="py-1"><Input value={s.pct} onChange={e => setServiceLines(p => p.map((x, j) => j === i ? { ...x, pct: e.target.value } : x))} className="h-6 text-[10px] text-center w-10" /></td>
            </tr>
          ))}</tbody></table>
        </SwissCard>
        <SwissCard className="p-4">
          <h3 className="text-sm font-bold mb-3">Revenue by Channel</h3>
          <table className="w-full text-xs"><thead><tr className="border-b border-gray-200">
            <th className="text-left py-1 text-[9px] text-gray-500 uppercase">Channel</th>
            <th className="text-center py-1 text-[9px] text-gray-500 uppercase w-24">Revenue</th>
            <th className="text-center py-1 text-[9px] text-gray-500 uppercase w-14">%</th>
          </tr></thead><tbody>{channels.map((c, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="py-1 font-semibold">{c.channel}</td>
              <td className="py-1"><Input value={c.revenue} onChange={e => setChannels(p => p.map((x, j) => j === i ? { ...x, revenue: e.target.value } : x))} className="h-6 text-[10px] text-center w-20" /></td>
              <td className="py-1"><Input value={c.pct} onChange={e => setChannels(p => p.map((x, j) => j === i ? { ...x, pct: e.target.value } : x))} className="h-6 text-[10px] text-center w-12" /></td>
            </tr>
          ))}</tbody></table>
        </SwissCard>
      </div>

      {/* Membership Revenue */}
      <SwissCard className="p-4">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-red-600" /> Membership Revenue</h3>
        <div className="grid grid-cols-3 gap-4 mb-3">
          {['lobby', 'condo', 'penthouse'].map(tier => (
            <div key={tier} className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-bold capitalize mb-2">{tier}</p>
              <div className="grid grid-cols-2 gap-2">
                <div><p className="text-[9px] text-gray-500">Members</p><Input value={membership[tier]?.members || ''} onChange={e => setMembership(p => ({ ...p, [tier]: { ...p[tier], members: e.target.value } }))} className="h-7 text-xs" /></div>
                <div><p className="text-[9px] text-gray-500">Revenue</p><Input value={membership[tier]?.revenue || ''} onChange={e => setMembership(p => ({ ...p, [tier]: { ...p[tier], revenue: e.target.value } }))} className="h-7 text-xs" /></div>
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><p className="text-[9px] text-gray-500 uppercase font-bold">Total MRR</p><Input value={membership.total_mrr || ''} onChange={e => setMembership(p => ({ ...p, total_mrr: e.target.value }))} className="h-8 text-xs font-bold" /></div>
          <div><p className="text-[9px] text-gray-500 uppercase font-bold">Total Members</p><Input value={membership.total_members || ''} onChange={e => setMembership(p => ({ ...p, total_members: e.target.value }))} className="h-8 text-xs font-bold" /></div>
        </div>
      </SwissCard>

      {/* Consulting + ACCD */}
      <div className="grid lg:grid-cols-2 gap-4">
        <SwissCard className="p-4">
          <h3 className="text-sm font-bold mb-3"><DollarSign className="inline h-4 w-4 text-red-600" /> Consulting Revenue</h3>
          <table className="w-full text-xs"><thead><tr className="border-b border-gray-200">
            <th className="text-left py-1 text-[9px] text-gray-500 uppercase">Track</th>
            <th className="text-center py-1 text-[9px] text-gray-500 uppercase w-16">Sessions</th>
            <th className="text-center py-1 text-[9px] text-gray-500 uppercase w-24">Revenue</th>
          </tr></thead><tbody>{consulting.map((c, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="py-1 font-semibold">{c.track}</td>
              <td className="py-1"><Input type="number" value={c.sessions} onChange={e => setConsulting(p => p.map((x, j) => j === i ? { ...x, sessions: e.target.value } : x))} className="h-6 text-[10px] text-center w-14" /></td>
              <td className="py-1"><Input value={c.revenue} onChange={e => setConsulting(p => p.map((x, j) => j === i ? { ...x, revenue: e.target.value } : x))} className="h-6 text-[10px] text-center w-20" /></td>
            </tr>
          ))}</tbody></table>
        </SwissCard>
        <SwissCard className="p-4">
          <h3 className="text-sm font-bold mb-3">ACCD & Driven Collective</h3>
          <div className="space-y-2 text-xs">
            {[{ label: 'ACCD New Enrollments', key: 'new_enrollments', type: 'number' }, { label: 'ACCD Active Students', key: 'active_students', type: 'number' }, { label: 'ACCD Revenue', key: 'revenue', type: 'text' },
              { label: 'DC Active Drivers', key: 'dc_active_drivers', type: 'number' }, { label: 'DC New Onboarded', key: 'dc_new_onboarded', type: 'number' }, { label: 'DC Hours Deployed', key: 'dc_hours', type: 'number' }, { label: 'DC Total Paid', key: 'dc_total_paid', type: 'text' },
            ].map((f, i) => (
              <div key={i} className="flex justify-between items-center">
                <span className="text-gray-500">{f.label}</span>
                <Input type={f.type} value={accd[f.key] || ''} onChange={e => setAccd(p => ({ ...p, [f.key]: e.target.value }))} className="h-7 text-xs w-24 text-center" />
              </div>
            ))}
          </div>
        </SwissCard>
      </div>

      {/* Lists */}
      {[{ title: 'Top Clients', items: topClients, set: setTopClients },
        { title: 'Outstanding Collections', items: collections, set: setCollections },
        { title: 'Month Notes', items: notes, set: setNotes },
      ].map((s, si) => (
        <SwissCard key={si} className="p-4">
          <h3 className="text-sm font-bold mb-2">{s.title}</h3>
          <div className="space-y-1">{s.items.map((item, i) => (
            <Input key={i} value={item} onChange={e => s.set(p => p.map((v, j) => j === i ? e.target.value : v))} placeholder={`${s.title.slice(0, -1)} ${i + 1}`} className="h-7 text-xs" />
          ))}</div>
        </SwissCard>
      ))}

      {/* Running YTD */}
      <SwissCard className="p-4 bg-[#0A0A0A] text-white">
        <h3 className="text-xs font-bold text-gray-400 uppercase mb-3"><TrendingUp className="inline h-3 w-3 mr-1" /> Year-to-Date Running Totals</h3>
        <div className="grid grid-cols-4 gap-3">
          {[{ label: 'Year', key: 'year', placeholder: '2026' }, { label: 'Gross YTD', key: 'gross_ytd', placeholder: '$0' }, { label: 'Net YTD', key: 'net_ytd', placeholder: '$0' }, { label: 'Remaining to Annual Goal', key: 'remaining', placeholder: '$0' }].map((f, i) => (
            <div key={i}>
              <p className="text-[9px] text-gray-500 uppercase mb-1">{f.label}</p>
              <Input value={running[f.key] || ''} onChange={e => setRunning(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} className="h-8 text-xs bg-white/10 border-white/20 text-white" />
            </div>
          ))}
        </div>
      </SwissCard>

      <div className="sticky bottom-16 sm:bottom-4 z-20 flex justify-center">
        <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700 shadow-xl text-sm h-10 px-8"><Save className="h-4 w-4 mr-2" /> Save Monthly P&L</Button>
      </div>
    </div>
  );
};

export default MonthlyPLTab;
